import * as React from 'react'

import Link from 'next/link'
import styled from 'styled-components'

import * as contentful from '@boutique/contentful'
import * as url from '@boutique/url'
import * as hooks from '@owl-nest/hooks'

import * as plume from '@ulule/owl-kit-components/next'

type PillarAccordionProps = {
  pillar: string
  kind: 'environmentalPillar' | 'socialPillar'
  criteria: {
    description: string
    icon: contentful.PillarIcon
    name: string
    slug: string
  }[]
}

export function PillarAccordion({
  pillar,
  criteria,
  kind,
}: PillarAccordionProps): React.ReactElement<PillarAccordionProps> {
  const [isOpen, setIsOpen] = React.useState(false)
  const isClient = hooks.useClientSide()

  return (
    <Accordion
      open={isOpen}
      title={pillar}
      onToggle={() => {
        if (isClient && plume.utils.isMobile()) {
          setIsOpen(!isOpen)
        }
      }}
    >
      <CriteriaList>
        {criteria.map((criterion) => {
          return (
            <CriteriaItem key={criterion.name}>
              <Link
                href={
                  url.products({
                    query: {
                      facetFilters: {
                        [kind]: criterion.slug,
                      },
                    },
                  }).path
                }
                prefetch={false}
                legacyBehavior
              >
                <a>
                  <CriteriaHead>
                    <CriteriaIcon icon={criterion.icon} size={25} />
                    <plume.styles.copy.S asDesktop="M">{criterion.name}</plume.styles.copy.S>
                  </CriteriaHead>
                </a>
              </Link>
              <Description as="p">{criterion.description}</Description>
            </CriteriaItem>
          )
        })}
      </CriteriaList>
    </Accordion>
  )
}

type CriteriaIconProps = {
  icon: contentful.PillarIcon
} & plume.illustrations.SpecificIllustrationProps

function CriteriaIcon({ icon, ...rest }: CriteriaIconProps) {
  switch (icon) {
    case 'carbon-neutral': {
      return <plume.illustrations.boutique.CarbonNeutral {...rest} />
    }
    case 'circular-economy': {
      return <plume.illustrations.boutique.CircularEconomy {...rest} />
    }
    case 'fair-trade': {
      return <plume.illustrations.boutique.FairTrade {...rest} />
    }
    case 'inventory-optimized': {
      return <plume.illustrations.boutique.OptimizedInventory {...rest} />
    }
    case 'made-in-europe': {
      return <plume.illustrations.boutique.MadeInEurope {...rest} />
    }
    case 'made-in-france': {
      return <plume.illustrations.boutique.MadeInFrance {...rest} />
    }
    case 'natural-materials': {
      return <plume.illustrations.boutique.NaturalMaterials {...rest} />
    }
    case 'organic': {
      return <plume.illustrations.boutique.Organic {...rest} />
    }
    case 'recycled-materials': {
      return <plume.illustrations.boutique.RecycledMaterials {...rest} />
    }
    case 'short-supply-chain': {
      return <plume.illustrations.boutique.ShortSupplyChain {...rest} />
    }
    case 'sustainable': {
      return <plume.illustrations.boutique.Sustainable {...rest} />
    }
    case 'upcycled-materials': {
      return <plume.illustrations.boutique.UpcycledMaterials {...rest} />
    }
    case 'zero-waste': {
      return <plume.illustrations.boutique.ZeroWaste {...rest} />
    }
  }
}

const CriteriaHead = styled.div`
  align-items: center;
  display: inline-flex;
  margin-bottom: 4px;

  ${plume.styles.copy.S} {
    margin-left: 10px;
    font-weight: 600;
  }
`

const CriteriaList = styled.ul``

const Description = styled(plume.styles.copy.S)`
  padding-left: 35px;
`

const CriteriaItem = styled.li`
  margin: 25px 0;
`

const Accordion = styled(plume.Accordion)`
  margin-top: 27px;
  padding: 0;
  border: none;

  ${plume.styles.accordion.Title} ${plume.styles.copy.Base} {
    color: ${plume.COLORS.SECONDARY_MINT};
    background-color: ${plume.COLORS.SECONDARY_GREEN_LIGHT};
    padding: 2px 8px;

    @media screen and ${plume.BREAKPOINTS.TABLET} {
      padding: 3px 10px;
      font-size: 18px;
    }
  }

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    ${plume.styles.accordion.Content} {
      max-height: max-content;
    }

    ${plume.glyphs.stroke.CaretUp}, ${plume.glyphs.stroke.CaretDown} {
      & {
        display: none;
      }
    }

    ${plume.styles.accordion.Toggler} {
      cursor: default;
    }
  }
`
